import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Plus, Edit2, Trash2, Eye, EyeOff, Users, ArrowRight, Mail, ChevronDown, ChevronUp, Copy, ImagePlus, X, UserPlus, Archive, ArchiveRestore, Clipboard, Check, RotateCcw, Pencil } from "lucide-react";
import ModernRichTextEditor from "../ModernRichTextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const genderLabel = (g) => {
  const v = (g || "").toLowerCase();
  if (["m", "junge", "männlich"].includes(v)) return "Junge";
  if (["w", "mädchen", "maedchen", "weiblich"].includes(v)) return "Mädchen";
  if (["d", "divers"].includes(v)) return "Divers";
  return g || "—";
};

export function CoursesTabModern({ authToken, groups, onChildrenImported }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegs, setLoadingRegs] = useState(false);
  const [importGroupId, setImportGroupId] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [showWidgetCode, setShowWidgetCode] = useState(false);
  const [lateralEntries, setLateralEntries] = useState([]);
  const [lateralFilter, setLateralFilter] = useState("active");
  const [showLateral, setShowLateral] = useState(false);
  const [showManualRegForm, setShowManualRegForm] = useState(false);
  const [manualRegSaving, setManualRegSaving] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(null); // { courseId, regId, childName }
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSaving, setCancelSaving] = useState(false);
  const [detailReg, setDetailReg] = useState(null); // selected registration for detail modal
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const headers = useCallback(() => ({
    headers: { Authorization: `Bearer ${authToken}` }
  }), [authToken]);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/courses`, headers());
      setCourses(res.data);
    } catch (err) {
      console.error("Fehler beim Laden der Kurse:", err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const fetchRegistrations = async (courseId) => {
    setLoadingRegs(true);
    try {
      const res = await axios.get(`${API_BASE}/api/courses/${courseId}/registrations`, headers());
      setRegistrations(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRegs(false);
    }
  };

  const handleSelectCourse = (course) => {
    if (selectedCourse?.id === course.id) {
      setSelectedCourse(null);
      setRegistrations([]);
    } else {
      setSelectedCourse(course);
      fetchRegistrations(course.id);
    }
    setShowManualRegForm(false);
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm("Kurs wirklich löschen?")) return;
    try {
      await axios.delete(`${API_BASE}/api/courses/${courseId}`, headers());
      fetchCourses();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
        setRegistrations([]);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleDuplicate = async (courseId) => {
    try {
      const res = await axios.post(`${API_BASE}/api/courses/${courseId}/duplicate`, {}, headers());
      await fetchCourses();
      const newCourse = res.data;
      setEditingCourse(newCourse);
      setShowForm(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Duplizieren");
    }
  };

  const handleImageUpload = async (courseId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API_BASE}/api/courses/${courseId}/image`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      });
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Hochladen");
    }
  };

  const handleImageDelete = async (courseId) => {
    if (!window.confirm("Teaserfoto entfernen?")) return;
    try {
      await axios.delete(`${API_BASE}/api/courses/${courseId}/image`, headers());
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    }
  };


  const fetchLateralEntries = useCallback(async (filter = lateralFilter) => {
    try {
      const res = await axios.get(`${API_BASE}/api/courses/lateral-entries?status_filter=${filter}`, headers());
      setLateralEntries(res.data);
    } catch (err) {
      console.error("Fehler beim Laden der Quereinstieg-Anfragen", err);
    }
  }, [headers, lateralFilter]);

  const updateLateralStatus = async (entryId, status) => {
    try {
      await axios.put(`${API_BASE}/api/courses/lateral-entries/${entryId}`, { status }, headers());
      fetchLateralEntries();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    }
  };

  const deleteLateralEntry = async (entryId) => {
    if (!window.confirm("Anfrage endgültig löschen?")) return;
    try {
      await axios.delete(`${API_BASE}/api/courses/lateral-entries/${entryId}`, headers());
      fetchLateralEntries();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    }
  };


  const handleActivate = async (courseId) => {
    try {
      await axios.post(`${API_BASE}/api/courses/${courseId}/activate`, {}, headers());
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    }
  };

  const handleDeactivate = async (courseId) => {
    try {
      await axios.post(`${API_BASE}/api/courses/${courseId}/deactivate`, {}, headers());
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    }
  };

  const handleCancelRegistration = async (courseId, regId) => {
    setCancelSaving(true);
    try {
      await axios.post(`${API_BASE}/api/courses/${courseId}/registrations/${regId}/cancel`, { reason: cancelReason }, headers());
      fetchRegistrations(courseId);
      fetchCourses();
      setCancelDialog(null);
      setCancelReason("");
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    } finally {
      setCancelSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!detailReg) return;
    setEditSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/api/courses/${detailReg.course_id}/registrations/${detailReg.id}`, editData, headers());
      setDetailReg(res.data);
      setEditMode(false);
      setEditData({});
      fetchRegistrations(detailReg.course_id);
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setEditSaving(false);
    }
  };

  const handleReactivateRegistration = async (courseId, regId, targetCourseId) => {
    try {
      await axios.post(
        `${API_BASE}/api/courses/${courseId}/registrations/${regId}/reactivate`,
        targetCourseId && targetCourseId !== courseId ? { target_course_id: targetCourseId } : {},
        headers()
      );
      fetchRegistrations(courseId);
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Reaktivieren");
    }
  };

  const handleDeleteRegistrationPermanent = async (courseId, regId) => {
    if (!window.confirm("Anmeldungsdaten endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden (DSGVO).")) return;
    try {
      await axios.delete(`${API_BASE}/api/courses/${courseId}/registrations/${regId}/permanent`, headers());
      fetchRegistrations(courseId);
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleImport = async () => {
    if (!importGroupId || !selectedCourse) return;
    if (!window.confirm("Alle bestätigten Anmeldungen in die gewählte Gruppe importieren?")) return;
    try {
      const res = await axios.post(
        `${API_BASE}/api/courses/${selectedCourse.id}/import-to-group`,
        { group_id: importGroupId },
        headers()
      );
      setImportResult(res.data);
      fetchRegistrations(selectedCourse.id);
      fetchCourses();
      if (onChildrenImported) onChildrenImported();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Import");
    }
  };

  const handleManualRegistration = async (formData) => {
    if (!selectedCourse) return;
    setManualRegSaving(true);
    try {
      await axios.post(
        `${API_BASE}/api/courses/${selectedCourse.id}/registrations`,
        formData,
        headers()
      );
      setShowManualRegForm(false);
      fetchRegistrations(selectedCourse.id);
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Anlegen der Anmeldung");
    } finally {
      setManualRegSaving(false);
    }
  };

  const handleArchive = async (courseId) => {
    try {
      await axios.post(`${API_BASE}/api/courses/${courseId}/archive`, {}, headers());
      fetchCourses();
      if (selectedCourse?.id === courseId) {
        setSelectedCourse(null);
        setRegistrations([]);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Archivieren");
    }
  };

  const handleUnarchive = async (courseId) => {
    try {
      await axios.post(`${API_BASE}/api/courses/${courseId}/unarchive`, {}, headers());
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Wiederherstellen");
    }
  };

  const statusBadge = (status) => {
    const map = {
      draft: { label: "Entwurf", cls: "bg-slate-100 text-slate-700" },
      active: { label: "Aktiv", cls: "bg-emerald-100 text-emerald-700" },
      closed: { label: "Geschlossen", cls: "bg-red-100 text-red-700" },
    };
    const s = map[status] || map.draft;
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  const regStatusBadge = (status) => {
    const map = {
      confirmed: { label: "Bestätigt", cls: "bg-emerald-100 text-emerald-700" },
      cancelled: { label: "Storniert", cls: "bg-red-100 text-red-700" },
      imported: { label: "Importiert", cls: "bg-blue-100 text-blue-700" },
    };
    const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return d; }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Lade Kurse...</div>;

  return (
    <div className="space-y-6" data-testid="courses-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Kursbuchung</h2>
          <p className="text-sm text-slate-500">Schwimmkurse anlegen, verwalten und Anmeldungen bearbeiten</p>
        </div>
        <Button
          data-testid="create-course-btn"
          onClick={() => { setEditingCourse(null); setShowForm(true); }}
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> Neuer Kurs
        </Button>
      </div>

      {/* Kurs-Formular */}
      {showForm && (
        <CourseForm
          course={editingCourse}
          authToken={authToken}
          onSave={async (data, pendingImageFile) => {
            try {
              let savedCourseId;
              if (editingCourse) {
                await axios.put(`${API_BASE}/api/courses/${editingCourse.id}`, data, headers());
                savedCourseId = editingCourse.id;
              } else {
                const res = await axios.post(`${API_BASE}/api/courses`, data, headers());
                savedCourseId = res.data.id;
              }
              if (pendingImageFile && savedCourseId) {
                const formData = new FormData();
                formData.append("file", pendingImageFile);
                await axios.post(`${API_BASE}/api/courses/${savedCourseId}/image`, formData, {
                  headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
                });
              }
              setShowForm(false);
              setEditingCourse(null);
              fetchCourses();
            } catch (err) {
              alert(err.response?.data?.detail || "Fehler beim Speichern");
            }
          }}
          onCancel={() => { setShowForm(false); setEditingCourse(null); }}
          onImageUpload={handleImageUpload}
          onImageDelete={handleImageDelete}
        />
      )}

      {/* Kursliste */}
      {courses.length === 0 && !showForm ? (
        <div className="rounded-xl bg-slate-50 p-12 text-center">
          <p className="text-lg text-slate-500">Noch keine Kurse vorhanden.</p>
          <p className="mt-2 text-sm text-slate-400">Erstellen Sie Ihren ersten Schwimmkurs.</p>
        </div>
      ) : (
        <>
          {/* Aktuelle Kurse */}
          {courses.filter(c => !c.is_past).length > 0 && (
            <div className="space-y-3">
              {courses.filter(c => !c.is_past).map((course) => (
            <div key={course.id} className="rounded-xl border bg-white shadow-sm overflow-hidden">
              {/* Kurs-Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => handleSelectCourse(course)}
                data-testid={`course-item-${course.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {statusBadge(course.status)}
                  <div className="min-w-0 flex items-center gap-3">
                    {course.has_image && (
                      <img
                        src={`${API_BASE}/api/courses/${course.id}/image`}
                        alt=""
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-slate-900 truncate">{course.title}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-0.5">
                        <span>Start: {formatDate(course.start_date)}</span>
                        {course.weekday_time && <span>{course.weekday_time}</span>}
                        <span className="flex items-center gap-1" data-testid={`course-spots-${course.id}`}>
                          <Users className="h-3.5 w-3.5" />
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                            (course.registered_count || 0) >= course.max_participants
                              ? "bg-red-100 text-red-700"
                              : (course.max_participants - (course.registered_count || 0)) <= 5
                                ? "bg-amber-100 text-amber-700"
                                : "bg-emerald-100 text-emerald-700"
                          }`}>
                            {course.registered_count || 0}/{course.max_participants} Plätze
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {course.status === "draft" && (
                    <Button size="sm" variant="outline" className="gap-1 text-emerald-600" onClick={(e) => { e.stopPropagation(); handleActivate(course.id); }}>
                      <Eye className="h-3.5 w-3.5" /> Aktivieren
                    </Button>
                  )}
                  {course.status === "active" && (
                    <Button size="sm" variant="outline" className="gap-1 text-amber-600" onClick={(e) => { e.stopPropagation(); handleDeactivate(course.id); }}>
                      <EyeOff className="h-3.5 w-3.5" /> Deaktivieren
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" title="Duplizieren" onClick={(e) => { e.stopPropagation(); handleDuplicate(course.id); }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingCourse(course); setShowForm(true); }}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" title="Archivieren" data-testid={`archive-course-${course.id}`} onClick={(e) => { e.stopPropagation(); handleArchive(course.id); }}>
                    <Archive className="h-4 w-4 text-slate-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); handleDelete(course.id); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  {selectedCourse?.id === course.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
              </div>

              {/* Anmeldungen (ausgeklappt) */}
              {selectedCourse?.id === course.id && (
                <div className="border-t bg-slate-50/50 p-4 space-y-4">
                  {/* Anmeldungen Header */}
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-700">
                      Anmeldungen ({registrations.filter(r => r.status !== "cancelled").length} aktiv / {registrations.length} gesamt)
                    </h4>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      data-testid="manual-reg-btn"
                      onClick={() => setShowManualRegForm(!showManualRegForm)}
                    >
                      {showManualRegForm ? <X className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                      {showManualRegForm ? "Abbrechen" : "Manuell hinzufügen"}
                    </Button>
                  </div>

                  {/* Manuelles Anmeldeformular */}
                  {showManualRegForm && (
                    <ManualRegistrationForm
                      onSubmit={handleManualRegistration}
                      saving={manualRegSaving}
                      onCancel={() => setShowManualRegForm(false)}
                    />
                  )}

                  {loadingRegs ? (
                    <p className="text-sm text-slate-400">Lade...</p>
                  ) : registrations.length === 0 ? (
                    <p className="text-sm text-slate-400">Noch keine Anmeldungen für diesen Kurs.</p>
                  ) : (
                    <div className="space-y-2">
                      {registrations.map((reg, idx) => (
                        <div
                          key={reg.id}
                          className={`rounded-lg border p-3 cursor-pointer transition-colors ${reg.status === "cancelled" ? "bg-red-50/40 border-red-100 opacity-60" : "bg-white hover:bg-slate-50 border-slate-200"}`}
                          onClick={() => setDetailReg(reg)}
                          data-testid={`reg-row-${reg.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            {/* Linke Seite: Infos */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-slate-400 font-mono w-5">{idx + 1}.</span>
                                <span className="font-semibold text-slate-900 text-sm">
                                  {reg.child_first_name} {reg.child_last_name}
                                </span>
                                {reg.notes && (
                                  <button
                                    type="button"
                                    title="Bemerkung vorhanden"
                                    className="inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                                    style={{ width: 18, height: 18, fontSize: 11, fontWeight: 700, flexShrink: 0 }}
                                    data-testid={`notes-btn-${reg.id}`}
                                    onClick={(e) => { e.stopPropagation(); setDetailReg(reg); }}
                                  >!</button>
                                )}
                                {regStatusBadge(reg.status)}
                              </div>
                              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                                <span>Geb. {formatDate(reg.child_birth_date)}</span>
                                <span>{reg.parent_first_name} {reg.parent_last_name}</span>
                                <span>{reg.phone}</span>
                                <span className="truncate max-w-[200px]">{reg.email}</span>
                              </div>
                            </div>

                            {/* Rechte Seite: Aktionen */}
                            <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              {reg.status === "confirmed" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 h-7 text-xs"
                                  data-testid={`cancel-reg-btn-${reg.id}`}
                                  onClick={() => setCancelDialog({ courseId: course.id, regId: reg.id, childName: `${reg.child_first_name} ${reg.child_last_name}` })}
                                >
                                  Stornieren
                                </Button>
                              )}
                              {reg.status === "cancelled" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600 h-7 gap-1 text-xs"
                                    title="Stornierung zurücknehmen"
                                    onClick={() => handleReactivateRegistration(course.id, reg.id)}
                                  >
                                    <RotateCcw className="h-3 w-3" /> Zurücknehmen
                                  </Button>
                                  {courses.filter(c => !c.is_past && c.id !== course.id).length > 0 && (
                                    <select
                                      className="h-7 rounded border border-slate-200 px-1 text-xs max-w-[120px]"
                                      defaultValue=""
                                      data-testid={`move-course-select-${reg.id}`}
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleReactivateRegistration(course.id, reg.id, e.target.value);
                                          e.target.value = "";
                                        }
                                      }}
                                    >
                                      <option value="" disabled>→ Kurs</option>
                                      {courses.filter(c => !c.is_past && c.id !== course.id).map(c => (
                                        <option key={c.id} value={c.id}>{c.title}</option>
                                      ))}
                                    </select>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 h-7"
                                    title="Endgültig löschen (DSGVO)"
                                    onClick={() => handleDeleteRegistrationPermanent(course.id, reg.id)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Import-Bereich */}
                  {registrations.some(r => r.status === "confirmed") && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                      <h5 className="font-semibold text-blue-800 flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" /> Kinder in Gruppe übernehmen
                      </h5>
                      <p className="text-sm text-blue-700">
                        Alle bestätigten Anmeldungen werden als neue Kinder (NEU) in die gewählte Gruppe importiert.
                      </p>
                      <div className="flex items-center gap-3">
                        <select
                          value={importGroupId}
                          onChange={(e) => setImportGroupId(e.target.value)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          data-testid="import-group-select"
                        >
                          <option value="">Gruppe wählen...</option>
                          {(groups || []).map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                        <Button
                          data-testid="import-children-btn"
                          onClick={handleImport}
                          disabled={!importGroupId}
                          className="gap-2"
                        >
                          <Users className="h-4 w-4" /> Importieren
                        </Button>
                      </div>
                      {importResult && (
                        <div className="rounded-lg bg-emerald-100 p-3 text-sm text-emerald-800">
                          {importResult.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
            </div>
          )}

          {/* Zurückliegende / Archivierte Kurse */}
          {courses.filter(c => c.is_past).length > 0 && (
            <>
              <div className="mt-8 mb-3 flex items-center gap-2">
                <div className="h-px flex-1 bg-slate-200"></div>
                <span className="text-sm font-medium text-slate-400 px-2">Archiv</span>
                <div className="h-px flex-1 bg-slate-200"></div>
              </div>
              <div className="space-y-3 opacity-70">
                {courses.filter(c => c.is_past).map((course) => (
                  <div key={course.id} className="rounded-xl border bg-white shadow-sm overflow-hidden">
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => handleSelectCourse(course)}
                      data-testid={`course-item-${course.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-500">
                          {course.archived ? "Archiviert" : "Zurückliegend"}
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-500 truncate">{course.title}</p>
                          <p className="text-xs text-slate-400">
                            Start: {formatDate(course.start_date)} · {course.registered_count || 0} Anmeldungen
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {course.archived && (
                          <Button size="sm" variant="ghost" title="Wiederherstellen" data-testid={`unarchive-course-${course.id}`} onClick={(e) => { e.stopPropagation(); handleUnarchive(course.id); }}>
                            <ArchiveRestore className="h-4 w-4 text-blue-500" />
                          </Button>
                        )}
                        {selectedCourse?.id === course.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </div>
                    </div>
                    {selectedCourse?.id === course.id && (
                      <div className="border-t bg-slate-50/50 p-4 space-y-4">
                        <div>
                          <h4 className="font-semibold text-slate-700">
                            Anmeldungen ({registrations.filter(r => r.status !== "cancelled").length} aktiv / {registrations.length} gesamt)
                          </h4>
                        </div>
                        {registrations.length === 0 ? (
                          <p className="text-sm text-slate-400">Keine Anmeldungen vorhanden.</p>
                        ) : (
                          <div className="space-y-2">
                            {registrations.map((r) => (
                              <div key={r.id} className={`flex items-center justify-between p-3 rounded-lg border ${r.status === "cancelled" ? "bg-red-50/50 border-red-100" : "bg-white"}`}>
                                <div>
                                  <p className="font-medium text-sm">{r.child_first_name} {r.child_last_name}</p>
                                  <p className="text-xs text-slate-400">{r.email} · Geb: {r.child_birth_date}</p>
                                </div>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}>
                                  {r.status === "cancelled" ? "Storniert" : "Aktiv"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* E-Mail-Vorlagen & Widget */}
      <div className="mt-6 flex flex-wrap gap-6">
        <button
          className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          onClick={async () => {
            if (!showEmailTemplate) {
              try {
                const res = await axios.get(`${API_BASE}/api/courses/email-templates`, headers());
                setEmailTemplates(res.data);
              } catch (err) { console.error(err); }
            }
            setShowEmailTemplate(!showEmailTemplate);
          }}
        >
          <Mail className="h-4 w-4" />
          E-Mail-Vorlagen
          {showEmailTemplate ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button
          className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          data-testid="widget-code-toggle"
          onClick={() => setShowWidgetCode(!showWidgetCode)}
        >
          Widget-Einbettungscode
          {showWidgetCode ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <a
          href="/kurse"
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 text-sm font-medium text-sky-600 hover:text-sky-800"
        >
          <Eye className="h-4 w-4" />
          Öffentliche Anmeldeseite
        </a>
      </div>

      {showEmailTemplate && emailTemplates.length > 0 && (
        <div className="space-y-4">
          {emailTemplates.map((tpl) => (
            <EmailTemplateEditor
              key={tpl.id}
              template={tpl}
              authToken={authToken}
              onSave={(updated) => {
                setEmailTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
              }}
            />
          ))}
        </div>
      )}

      {showWidgetCode && (
        <WidgetCodeSection />
      )}

      {/* Quereinstieg-Anfragen */}
      <div className="mt-4">
        <Button
          variant="outline"
          className="w-full flex items-center justify-between"
          data-testid="lateral-entries-toggle-btn"
          onClick={() => {
            if (!showLateral) fetchLateralEntries("active");
            setShowLateral(!showLateral);
          }}
        >
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Quereinstieg-Anfragen
            {lateralEntries.length > 0 && lateralFilter === "active" && (
              <Badge variant="secondary" className="ml-1">{lateralEntries.length}</Badge>
            )}
          </span>
          {showLateral ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {showLateral && (
        <div className="rounded-xl border bg-white p-4 shadow-sm space-y-4" data-testid="lateral-entries-section">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setLateralFilter("active"); fetchLateralEntries("active"); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${lateralFilter === "active" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              data-testid="lateral-filter-active"
            >
              Aktiv
            </button>
            <button
              onClick={() => { setLateralFilter("archived"); fetchLateralEntries("archived"); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${lateralFilter === "archived" ? "bg-slate-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              data-testid="lateral-filter-archived"
            >
              Archiv
            </button>
          </div>

          {lateralEntries.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">
              {lateralFilter === "active" ? "Keine offenen Quereinstieg-Anfragen." : "Keine archivierten Anfragen."}
            </p>
          ) : (
            <div className="space-y-3">
              {lateralEntries.map((entry) => {
                const created = entry.created_at ? new Date(entry.created_at) : null;
                const daysAgo = created ? Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)) : null;
                const dateStr = created ? created.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–";

                const statusColors = {
                  open: "bg-amber-100 text-amber-700",
                  contacted: "bg-blue-100 text-blue-700",
                  accepted: "bg-green-100 text-green-700",
                  rejected: "bg-red-100 text-red-600",
                  archived: "bg-slate-100 text-slate-500",
                };
                const statusLabels = {
                  open: "Offen",
                  contacted: "Kontaktiert",
                  accepted: "Aufgenommen",
                  rejected: "Abgelehnt",
                  archived: "Archiviert",
                };

                return (
                  <div key={entry.id} className="border rounded-lg p-4 bg-white" data-testid={`lateral-entry-${entry.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm text-slate-800">
                            {entry.child_first_name} {entry.child_last_name}
                          </p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[entry.status] || statusColors.open}`}>
                            {statusLabels[entry.status] || entry.status}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          Geb.: {entry.child_birth_date} · Anfrage vom {dateStr}
                          {daysAgo !== null && <span className="ml-1 text-slate-300">({daysAgo === 0 ? "heute" : `vor ${daysAgo} Tagen`})</span>}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Kurs: {entry.course_title}
                        </p>
                        <p className="text-xs text-slate-500">
                          Eltern: {entry.parent_first_name} {entry.parent_last_name} · {entry.email} · {entry.phone}
                        </p>
                        <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-600 italic">
                          &bdquo;{entry.experience_description}&ldquo;
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {entry.status === "open" && (
                        <button onClick={() => updateLateralStatus(entry.id, "contacted")} className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium">
                          Kontaktiert
                        </button>
                      )}
                      {(entry.status === "open" || entry.status === "contacted") && (
                        <>
                          <button onClick={() => updateLateralStatus(entry.id, "accepted")} className="text-xs px-3 py-1 rounded-full bg-green-50 text-green-600 hover:bg-green-100 font-medium">
                            Aufnehmen
                          </button>
                          <button onClick={() => updateLateralStatus(entry.id, "rejected")} className="text-xs px-3 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100 font-medium">
                            Ablehnen
                          </button>
                          <button onClick={() => updateLateralStatus(entry.id, "archived")} className="text-xs px-3 py-1 rounded-full bg-slate-50 text-slate-500 hover:bg-slate-100 font-medium">
                            Archivieren
                          </button>
                        </>
                      )}
                      {(entry.status === "archived" || entry.status === "accepted" || entry.status === "rejected") && (
                        <button onClick={() => deleteLateralEntry(entry.id)} className="text-xs px-3 py-1 rounded-full bg-red-50 text-red-500 hover:bg-red-100 font-medium">
                          Löschen
                        </button>
                      )}
                      <a href={`mailto:${entry.email}`} className="text-xs px-3 py-1 rounded-full bg-slate-50 text-slate-600 hover:bg-slate-100 font-medium flex items-center gap-1">
                        <Mail className="h-3 w-3" /> E-Mail
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Stornierung-Dialog mit Grundangabe */}
      {cancelDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setCancelDialog(null); setCancelReason(""); }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="cancel-dialog">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Anmeldung stornieren</h3>
            <p className="text-sm text-slate-500 mb-4">
              Stornierung für <strong>{cancelDialog.childName}</strong>
            </p>
            <div className="mb-4">
              <Label className="mb-1.5 block text-sm font-medium text-slate-700">Stornogrund (optional)</Label>
              <textarea
                data-testid="cancel-reason-input"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 min-h-[80px] resize-y"
                placeholder="z.B. Nach Telefonanruf vom..., E-Mail vom..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => { setCancelDialog(null); setCancelReason(""); }} data-testid="cancel-dialog-dismiss">
                Abbrechen
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={cancelSaving}
                data-testid="cancel-dialog-confirm"
                onClick={() => handleCancelRegistration(cancelDialog.courseId, cancelDialog.regId)}
              >
                {cancelSaving ? "Wird storniert..." : "Jetzt stornieren"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail-Modal für Anmeldung (Lese- und Bearbeitungsmodus) */}
      {detailReg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setDetailReg(null); setEditMode(false); setEditData({}); }}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reg-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">{editMode ? "Anmeldung bearbeiten" : "Anmeldungsdetails"}</h3>
              <div className="flex items-center gap-2">
                {!editMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="edit-reg-btn"
                    onClick={() => {
                      const normalizeGender = (g) => {
                        const v = (g || "").toLowerCase();
                        if (["m", "junge", "männlich"].includes(v)) return "Junge";
                        if (["w", "mädchen", "maedchen", "weiblich"].includes(v)) return "Mädchen";
                        if (["d", "divers"].includes(v)) return "Divers";
                        return "";
                      };
                      setEditMode(true);
                      setEditData({
                        child_first_name: detailReg.child_first_name || "",
                        child_last_name: detailReg.child_last_name || "",
                        child_birth_date: detailReg.child_birth_date || "",
                        gender: normalizeGender(detailReg.gender),
                        parent_first_name: detailReg.parent_first_name || "",
                        parent_last_name: detailReg.parent_last_name || "",
                        email: detailReg.email || "",
                        email2: detailReg.email2 || "",
                        phone: detailReg.phone || "",
                        phone2: detailReg.phone2 || "",
                        phone3: detailReg.phone3 || "",
                        notes: detailReg.notes || "",
                      });
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                  </Button>
                )}
                <button onClick={() => { setDetailReg(null); setEditMode(false); setEditData({}); }} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
            </div>

            {editMode ? (
              <div className="space-y-4">
                <div className="rounded-xl bg-slate-50 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Kind</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Vorname *</label>
                      <input data-testid="edit-child-first-name" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.child_first_name} onChange={(e) => setEditData({...editData, child_first_name: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Nachname *</label>
                      <input data-testid="edit-child-last-name" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.child_last_name} onChange={(e) => setEditData({...editData, child_last_name: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Geburtsdatum *</label>
                      <input data-testid="edit-birth-date" type="date" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.child_birth_date} onChange={(e) => setEditData({...editData, child_birth_date: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Geschlecht *</label>
                      <select data-testid="edit-gender" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.gender} onChange={(e) => setEditData({...editData, gender: e.target.value})}>
                        <option value="">Bitte wählen</option>
                        <option value="Junge">Junge</option>
                        <option value="Mädchen">Mädchen</option>
                        <option value="Divers">Divers</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Kontaktdaten</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Vorname Buchende/r *</label>
                      <input data-testid="edit-parent-first-name" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.parent_first_name} onChange={(e) => setEditData({...editData, parent_first_name: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Nachname Buchende/r *</label>
                      <input data-testid="edit-parent-last-name" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.parent_last_name} onChange={(e) => setEditData({...editData, parent_last_name: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">E-Mail *</label>
                      <input data-testid="edit-email" type="email" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">E-Mail 2</label>
                      <input data-testid="edit-email2" type="email" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.email2} onChange={(e) => setEditData({...editData, email2: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Telefon *</label>
                      <input data-testid="edit-phone" type="tel" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Telefon 2</label>
                      <input data-testid="edit-phone2" type="tel" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.phone2} onChange={(e) => setEditData({...editData, phone2: e.target.value})} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-slate-500 mb-1 block">Telefon 3</label>
                      <input data-testid="edit-phone3" type="tel" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" value={editData.phone3} onChange={(e) => setEditData({...editData, phone3: e.target.value})} />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-4 space-y-2">
                  <label className="text-xs text-slate-500 mb-1 block">Anmerkungen</label>
                  <textarea data-testid="edit-notes" className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 min-h-[60px] resize-y" value={editData.notes} onChange={(e) => setEditData({...editData, notes: e.target.value})} />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditMode(false); setEditData({}); }} data-testid="edit-cancel-btn">Abbrechen</Button>
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" disabled={editSaving} onClick={handleSaveEdit} data-testid="edit-save-btn">
                    {editSaving ? "Speichert..." : "Speichern"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Kind */}
                <div className="rounded-xl bg-slate-50 p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Kind</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-400">Name:</span> <strong>{detailReg.child_first_name} {detailReg.child_last_name}</strong></div>
                    <div><span className="text-slate-400">Geburtsdatum:</span> {formatDate(detailReg.child_birth_date)}</div>
                    <div><span className="text-slate-400">Geschlecht:</span> {genderLabel(detailReg.gender)}</div>
                    <div><span className="text-slate-400">Status:</span> {regStatusBadge(detailReg.status)}</div>
                  </div>
                </div>

                {/* Eltern / Kontakt */}
                <div className="rounded-xl bg-slate-50 p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Kontaktdaten</h4>
                  <div className="grid grid-cols-1 gap-1.5 text-sm">
                    <div><span className="text-slate-400">Eltern:</span> <strong>{detailReg.parent_first_name} {detailReg.parent_last_name}</strong></div>
                    <div><span className="text-slate-400">E-Mail:</span> <a href={`mailto:${detailReg.email}`} className="text-sky-600 hover:underline">{detailReg.email}</a></div>
                    {detailReg.email2 && <div><span className="text-slate-400">E-Mail 2:</span> <a href={`mailto:${detailReg.email2}`} className="text-sky-600 hover:underline">{detailReg.email2}</a></div>}
                    <div><span className="text-slate-400">Telefon:</span> <a href={`tel:${detailReg.phone}`} className="text-sky-600 hover:underline">{detailReg.phone}</a></div>
                    {detailReg.phone2 && <div><span className="text-slate-400">Telefon 2:</span> <a href={`tel:${detailReg.phone2}`} className="text-sky-600 hover:underline">{detailReg.phone2}</a></div>}
                    {detailReg.phone3 && <div><span className="text-slate-400">Telefon 3:</span> <a href={`tel:${detailReg.phone3}`} className="text-sky-600 hover:underline">{detailReg.phone3}</a></div>}
                  </div>
                </div>

                {/* Anmerkungen */}
                {detailReg.notes && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-1">
                    <h4 className="text-sm font-semibold text-amber-700 uppercase tracking-wide">Anmerkungen</h4>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">{detailReg.notes}</p>
                  </div>
                )}

                {/* Stornierungsinfo */}
                {detailReg.status === "cancelled" && (
                  <div className="rounded-xl bg-red-50 border border-red-200 p-4 space-y-1">
                    <h4 className="text-sm font-semibold text-red-700 uppercase tracking-wide">Stornierung</h4>
                    <div className="text-sm text-red-800 space-y-1">
                      <div><span className="text-red-400">Storniert am:</span> {formatDate(detailReg.cancelled_at)}</div>
                      <div><span className="text-red-400">Storniert durch:</span> {detailReg.cancelled_by === "admin" ? "Verwaltung" : "Eltern"}</div>
                      {detailReg.cancellation_reason && <div><span className="text-red-400">Grund:</span> {detailReg.cancellation_reason}</div>}
                    </div>
                  </div>
                )}

                {/* Meta */}
                <div className="text-xs text-slate-400 space-y-0.5 pt-2 border-t">
                  <div>Angemeldet am: {detailReg.created_at ? new Date(detailReg.created_at).toLocaleString("de-DE") : "—"}</div>
                  <div>Registriert durch: {detailReg.registered_by === "admin" ? "Verwaltung" : "Online-Buchung"}</div>
                  {detailReg.reactivated_at && <div>Reaktiviert am: {new Date(detailReg.reactivated_at).toLocaleString("de-DE")}</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function WidgetCodeSection() {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
  const scriptUrl = `${API_BASE}/api/courses/public/widget.js`;
  const courseUrl = `${window.location.origin}/kurse`;
  const [copied, setCopied] = useState(false);

  const embedCode = `<!-- Schwimmkurs-Buchungswidget - DIE FITMACHER -->\n<div id="sfw-kurse-widget">\n  <script src="${scriptUrl}" data-api="${scriptUrl}" data-url="${courseUrl}"><\/script>\n</div>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-4 rounded-xl border bg-white shadow-sm p-5 space-y-4" data-testid="widget-code-section">
      <p className="text-sm text-slate-600">
        Kopieren Sie den folgenden Code und fügen Sie ihn auf Ihrer Homepage ein. Das Widget wird sich nahtlos einfügen — kein iFrame.
      </p>
      <div className="rounded-lg bg-slate-900 p-4 overflow-x-auto">
        <pre className="text-xs text-green-400 whitespace-pre-wrap break-all font-mono leading-relaxed">{embedCode}</pre>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={handleCopy} data-testid="widget-copy-btn" className="gap-1.5">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />}
          {copied ? "Kopiert!" : "Code kopieren"}
        </Button>
        <span className="text-xs text-slate-400">Nach dem Deployment wird die Live-URL automatisch verwendet.</span>
      </div>
    </div>
  );
}


function ManualRegistrationForm({ onSubmit, saving, onCancel }) {
  const [form, setForm] = useState({
    child_first_name: "",
    child_last_name: "",
    child_birth_date: "",
    gender: "",
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-4" data-testid="manual-reg-form">
      <h5 className="font-semibold text-sm text-violet-900 mb-3 flex items-center gap-2">
        <UserPlus className="h-4 w-4" /> Manuelle Anmeldung
      </h5>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Vorname Kind *</Label>
            <Input
              data-testid="manual-reg-child-first"
              value={form.child_first_name}
              onChange={(e) => setForm({ ...form, child_first_name: e.target.value })}
              placeholder="Vorname"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Nachname Kind *</Label>
            <Input
              data-testid="manual-reg-child-last"
              value={form.child_last_name}
              onChange={(e) => setForm({ ...form, child_last_name: e.target.value })}
              placeholder="Nachname"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Geburtsdatum Kind *</Label>
            <Input
              data-testid="manual-reg-child-birth"
              type="date"
              value={form.child_birth_date}
              onChange={(e) => setForm({ ...form, child_birth_date: e.target.value })}
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Geschlecht *</Label>
            <select
              data-testid="manual-reg-gender"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              required
            >
              <option value="">Bitte wählen</option>
              <option value="Junge">Junge</option>
              <option value="Mädchen">Mädchen</option>
              <option value="Divers">Divers</option>
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">E-Mail *</Label>
            <Input
              data-testid="manual-reg-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="eltern@beispiel.de"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Vorname Elternteil *</Label>
            <Input
              data-testid="manual-reg-parent-first"
              value={form.parent_first_name}
              onChange={(e) => setForm({ ...form, parent_first_name: e.target.value })}
              placeholder="Vorname"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Nachname Elternteil *</Label>
            <Input
              data-testid="manual-reg-parent-last"
              value={form.parent_last_name}
              onChange={(e) => setForm({ ...form, parent_last_name: e.target.value })}
              placeholder="Nachname"
              required
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Telefon *</Label>
            <Input
              data-testid="manual-reg-phone"
              value={form.phone}
              onChange={(e) => {
                let d = e.target.value.replace(/[^\d+]/g, "");
                if (d.startsWith("0")) d = "+49" + d.substring(1);
                else if (d.startsWith("49")) d = "+" + d;
                else if (d && !d.startsWith("+")) d = "+49" + d;
                const digits = d.replace(/[^\d]/g, "");
                if (digits.length <= 2) { setForm({ ...form, phone: d }); return; }
                let r = digits.substring(2);
                const p = [r.substring(0, 3)];
                r = r.substring(3);
                while (r.length > 4) { p.push(r.substring(0, 4)); r = r.substring(4); }
                if (r) p.push(r);
                setForm({ ...form, phone: "+49 " + p.join(" ") });
              }}
              placeholder="+49 170 1234 5678"
              required
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>Abbrechen</Button>
          <Button type="submit" size="sm" disabled={saving} data-testid="manual-reg-submit">
            {saving ? "Speichere..." : "Anmeldung anlegen"}
          </Button>
        </div>
      </form>
    </div>
  );
}


function CourseForm({ course, authToken, onSave, onCancel, onImageUpload, onImageDelete }) {
  const [form, setForm] = useState({
    title: course?.title || "",
    description_html: course?.description_html || "",
    start_date: course?.start_date || "",
    weekday_time: course?.weekday_time || "",
    max_participants: course?.max_participants || 24,
    min_age_years: course?.min_age_years || 5.5,
    max_age_years: course?.max_age_years || 0,
    registration_deadline_days: course?.registration_deadline_days || 3,
    cancellation_deadline_days: course?.cancellation_deadline_days || 3,
    contact_email: course?.contact_email || "schwimmausbildung@sfw-berlin.org",
    location: course?.location || "Fritz-Wildung-Straße 7, 14199 Berlin",
  });
  const [saving, setSaving] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [hasImage, setHasImage] = useState(course?.has_image || false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (course?.id) {
      onImageUpload(course.id, file);
      setHasImage(true);
    } else {
      setPendingImage(file);
      setPendingPreview(URL.createObjectURL(file));
    }
  };

  const handleImageRemove = () => {
    if (course?.id && hasImage) {
      onImageDelete(course.id);
      setHasImage(false);
    } else if (pendingImage) {
      setPendingImage(null);
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
      setPendingPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form, pendingImage);
    } finally {
      setSaving(false);
    }
  };

  const imageUrl = course?.id && hasImage
    ? `${API_BASE}/api/courses/${course.id}/image`
    : pendingPreview;

  return (
    <div className="rounded-xl border bg-white p-6 shadow-sm" data-testid="course-form">
      <h3 className="text-lg font-bold text-slate-900 mb-4">
        {course ? "Kurs bearbeiten" : "Neuen Kurs anlegen"}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Teaserfoto */}
        <div>
          <Label className="mb-2 block">Teaserfoto</Label>
          <div className="flex items-center gap-4">
            {imageUrl ? (
              <div className="relative group">
                <img
                  src={imageUrl}
                  alt="Teaserfoto"
                  className="w-40 h-28 rounded-lg object-cover border"
                />
                <button
                  type="button"
                  onClick={handleImageRemove}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid="course-image-remove-btn"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-40 h-28 rounded-lg border-2 border-dashed border-slate-300 cursor-pointer hover:border-sky-400 transition-colors" data-testid="course-image-upload-area">
                <ImagePlus className="h-6 w-6 text-slate-400" />
                <span className="text-xs text-slate-400 mt-1">Foto hochladen</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} data-testid="course-image-input" />
              </label>
            )}
            {imageUrl && (
              <label className="text-sm text-sky-600 hover:text-sky-800 cursor-pointer font-medium">
                Foto ändern
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">Wird auf der öffentlichen Anmeldeseite angezeigt.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Kurstitel *</Label>
            <Input
              data-testid="course-title-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="z.B. Schwimmausbildung 1-2027"
              required
            />
          </div>
          <div>
            <Label>Startdatum *</Label>
            <Input
              data-testid="course-start-date-input"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              required
            />
          </div>
          <div>
            <Label>Tag & Uhrzeit</Label>
            <Input
              value={form.weekday_time}
              onChange={(e) => setForm({ ...form, weekday_time: e.target.value })}
              placeholder="z.B. Samstags 13:15 - 13:45 Uhr"
            />
          </div>
          <div>
            <Label>Max. Teilnehmer</Label>
            <Input
              data-testid="course-max-participants-input"
              type="number"
              value={form.max_participants}
              onChange={(e) => setForm({ ...form, max_participants: parseInt(e.target.value) || 24 })}
              min="1" max="100"
            />
          </div>
          <div>
            <Label>Mindestalter (Jahre)</Label>
            <Input
              type="number"
              value={form.min_age_years}
              onChange={(e) => setForm({ ...form, min_age_years: parseFloat(e.target.value) || 5.5 })}
              step="0.5" min="0" max="18"
            />
          </div>
          <div>
            <Label>Höchstalter (Jahre, 0 = unbegrenzt)</Label>
            <Input
              data-testid="course-max-age-input"
              type="number"
              value={form.max_age_years}
              onChange={(e) => setForm({ ...form, max_age_years: parseFloat(e.target.value) || 0 })}
              step="0.5" min="0" max="18"
            />
          </div>
          <div>
            <Label>Anmeldeschluss (Tage vor Beginn)</Label>
            <Input
              type="number"
              value={form.registration_deadline_days}
              onChange={(e) => setForm({ ...form, registration_deadline_days: parseInt(e.target.value) || 3 })}
              min="0" max="90"
            />
          </div>
          <div>
            <Label>Stornierungsfrist (Tage vor Beginn)</Label>
            <Input
              type="number"
              value={form.cancellation_deadline_days}
              onChange={(e) => setForm({ ...form, cancellation_deadline_days: parseInt(e.target.value) || 3 })}
              min="0" max="90"
            />
          </div>
          <div>
            <Label>Kontakt-E-Mail</Label>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
          </div>
          <div>
            <Label>Ort / Adresse</Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="z.B. Fritz-Wildung-Straße 7, 14199 Berlin"
            />
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Beschreibung</Label>
          <ModernRichTextEditor
            value={form.description_html}
            onChange={(html) => setForm((prev) => ({ ...prev, description_html: html }))}
            placeholder="Kursbeschreibung eingeben..."
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>Abbrechen</Button>
          <Button type="submit" disabled={saving} data-testid="course-save-btn">
            {saving ? "Speichere..." : (course ? "Änderungen speichern" : "Kurs erstellen")}
          </Button>
        </div>
      </form>
    </div>
  );
}


function EmailTemplateEditor({ template, authToken, onSave }) {
  const [subject, setSubject] = useState(template?.subject || "");
  const [html, setHtml] = useState(template?.html || "");
  const [saving, setSaving] = useState(false);

  const editorPlaceholders = (template?.placeholders || []).map((p) => ({
    key: p.key,
    label: p.label || p.key,
    description: p.label || p.key,
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE}/api/courses/email-templates/${template.id}`,
        { subject, html },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      onSave(res.data);
      alert("Vorlage gespeichert!");
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-xl border bg-white p-5 space-y-4">
      <h4 className="font-semibold text-slate-900">{template.name || "E-Mail-Vorlage"}</h4>
      <div>
        <Label>Betreff</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div>
        <Label className="mb-2 block">Inhalt (HTML)</Label>
        <ModernRichTextEditor
          value={html}
          onChange={setHtml}
          placeholders={editorPlaceholders}
          showPlaceholders={true}
        />
      </div>
      <Button onClick={handleSave} disabled={saving}>
        {saving ? "Speichere..." : "Vorlage speichern"}
      </Button>
    </div>
  );
}
